import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Progress,
  ResponseErrorPanel,
  InfoCard,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { Link as RouterLink } from 'react-router-dom';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormHelperText,
  Grid,
  Link,
  makeStyles,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import { useSignal } from '@backstage/plugin-signals-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  ACCESS_RESOURCE_SIGNAL_CHANNEL,
  ACCESS_RESOURCE_NAME_MAX_LENGTH,
  ACCESS_RESOURCE_NAME_RULE_DESCRIPTION,
  AccessBindingResource,
  AccessBindingSpec,
  AccessBindingSubject,
  AccessResourceStatus,
  AccessReconcileMaterialization,
  AccessResourceSignal,
  AccessRoleResource,
  accessBindingCreatePermission,
  accessBindingDeletePermission,
  accessBindingUpdatePermission,
  accessRoleUpdatePermission,
  accessReconcileRunPermission,
  validateAccessResourceName,
} from 'adp-access-common';
import { accessApiRef } from '../api';
import { AccessStatusChip } from './AccessStatusChip';
import { AccessToast, AccessToastState } from './AccessToast';
import {
  getManagedResourceKey,
  getManagedResourceSignalKey,
  isSignalNewerThanPrevious,
  patchManagedResourceBySignal,
  ProcessedAccessSignal,
  removeManagedResourceBySignal,
  upsertManagedResource,
} from './accessSignals';
import { accessTranslationRef } from '../translation';
import { buildRbacRolePath } from './accessNavigation';
import { deleteBindingAndReconcile } from './accessBindingActions';
import {
  buildBindingTargetRef,
  filterRelatedProjectOptions,
  getBindingTargetKind,
  resolveBindingFormTarget,
} from './accessBindingTargets';
import type { BindingTargetKind } from './accessBindingTargets';
import { validateAccessBindingRequiredFields } from './accessBindingValidation';

const PAGE_SIZE = 20;

type SortDirection = 'asc' | 'desc';
type SortField = 'name' | 'role';
type BindingGroupBy = 'none' | 'phase' | 'target' | 'role';
type BindingFilterValue = 'all';

type BindingFormState = {
  name: string;
  description: string;
  subjectRefs: string[];
  roleName: string;
  targetKind: BindingTargetKind;
  targetRef: string;
};

type CatalogSubjectOption = {
  kind: 'Group' | 'User';
  ref: string;
  label: string;
};

type CatalogComponentOption = {
  ref: string;
  label: string;
};

type CatalogClusterOption = {
  ref: string;
  label: string;
};

type CatalogProjectOption = {
  ref: string;
  label: string;
};

type AccessBindingsManagerProps = {
  title?: string;
  filterTargetRef?: string;
  filterSubjectRef?: string;
  lockTargetRef?: boolean;
  defaultTargetRef?: string;
  relatedProjectRefs?: readonly string[];
  allowedTargetKinds?: readonly BindingTargetKind[];
  initialSubjectRef?: string;
  routeBindingId?: string;
  routeMode?: 'list' | 'detail' | 'edit' | 'create';
  roleEditHref?: (roleName: string) => string;
  onEditBindingNavigate?: (
    binding: AccessBindingResource,
    mode: 'detail' | 'edit',
  ) => void;
  onCloseBindingNavigate?: () => void;
};

type BindingSignalDebugState = {
  lastRevision?: string;
  lastEvent?: string;
  lastResourceRef?: string;
  lastReceivedAt?: string;
  lastFetchStartedAt?: string;
  lastFetchFinishedAt?: string;
  lastFetchedPhase?: string;
  lastAppliedPhase?: string;
  lastAppliedName?: string;
  lastError?: string;
};

type GroupedBindings = {
  key: string;
  label: string;
  failedCount: number;
  items: AccessBindingResource[];
};

const subjectFilterOptions = createFilterOptions<CatalogSubjectOption>({
  stringify: option => `${option.label} ${option.ref} ${option.kind}`,
});

const useStyles = makeStyles(theme => ({
  form: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  nameRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  nameField: {
    flex: 1,
  },
  nameAction: {
    marginTop: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  helperText: {
    whiteSpace: 'pre-wrap',
  },
  jsonPreview: {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
  },
  tableActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  bindingsControls: {
    display: 'grid',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  bindingsFiltersRow: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns:
      'repeat(4, minmax(120px, 1fr)) minmax(220px, 1.4fr) minmax(120px, 1fr)',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  groupRow: {
    backgroundColor: theme.palette.action.hover,
  },
  groupText: {
    fontWeight: 700,
  },
  drawerPaper: {
    width: 1024,
    maxWidth: '100vw',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  drawerContent: {
    padding: theme.spacing(2),
  },
  highlightedRow: {
    '& > *': {
      animation: '$rowFlash 1.8s ease-out',
    },
  },
  statusCell: {
    width: 240,
    maxWidth: 240,
    overflow: 'hidden',
    verticalAlign: 'middle',
  },
  actionsCell: {
    width: 1,
    whiteSpace: 'nowrap',
  },
  bindingErrorText: {
    display: 'block',
    maxWidth: 220,
    marginTop: theme.spacing(0.5),
    fontSize: 11,
    lineHeight: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '@keyframes rowFlash': {
    '0%': {
      backgroundColor: theme.palette.warning.light,
    },
    '100%': {
      backgroundColor: 'inherit',
    },
  },
}));

function normalizeEntityRef(ref: string): string {
  return ref.trim().toLocaleLowerCase('en-US');
}

function bindingToFormState(
  binding?: AccessBindingResource,
  lockedTargetRef?: string,
  initialSubjectRef?: string,
  defaultTargetRef?: string,
  projectRefs: readonly string[] = [],
): BindingFormState {
  const { targetKind, targetRef } = resolveBindingFormTarget(
    binding,
    lockedTargetRef,
    defaultTargetRef,
    projectRefs,
  );
  const subjectRefs = (binding?.spec.subjects ?? []).map(
    subject => subject.ref,
  );

  return {
    name: binding?.metadata.name ?? '',
    description: binding?.metadata.description ?? '',
    subjectRefs:
      initialSubjectRef && !binding ? [initialSubjectRef] : subjectRefs,
    roleName: binding?.spec.roleRef.name ?? '',
    targetKind,
    targetRef,
  };
}

function buildSubjectRef(option: {
  kind?: string;
  metadata?: { namespace?: string; name?: string };
  spec?: { profile?: { displayName?: string } };
}): CatalogSubjectOption | undefined {
  const entityKind =
    option.kind === 'User' || option.kind === 'Group' ? option.kind : undefined;
  const name = option.metadata?.name;

  if (!entityKind || !name) {
    return undefined;
  }

  const namespace = option.metadata?.namespace ?? 'default';
  const displayName = option.spec?.profile?.displayName;

  return {
    kind: entityKind,
    ref: `${entityKind.toLowerCase()}:${namespace}/${name}`,
    label: displayName
      ? `${entityKind}: ${displayName}`
      : `${entityKind}: ${name}`,
  };
}

function buildComponentRef(option: {
  kind?: string;
  metadata?: { namespace?: string; name?: string; title?: string };
}): CatalogComponentOption | undefined {
  if (option.kind !== 'Component' || !option.metadata?.name) {
    return undefined;
  }

  const namespace = option.metadata.namespace ?? 'default';
  const title = option.metadata.title ?? option.metadata.name;
  const ref = `component:${namespace}/${option.metadata.name}`;

  return {
    ref,
    label: title,
  };
}

function buildClusterRef(option: {
  kind?: string;
  metadata?: { namespace?: string; name?: string; title?: string };
  spec?: { type?: string };
}): CatalogClusterOption | undefined {
  if (
    option.kind !== 'Resource' ||
    option.spec?.type !== 'kubernetes-cluster' ||
    !option.metadata?.name
  ) {
    return undefined;
  }

  const namespace = option.metadata.namespace ?? 'default';
  const title = option.metadata.title ?? option.metadata.name;
  const ref = `resource:${namespace}/${option.metadata.name}`;

  return {
    ref,
    label: title,
  };
}

function buildProjectRef(option: {
  kind?: string;
  metadata?: { namespace?: string; name?: string; title?: string };
  spec?: { type?: string };
}): CatalogProjectOption | undefined {
  if (
    option.kind !== 'Resource' ||
    option.spec?.type !== 'project' ||
    !option.metadata?.name
  ) {
    return undefined;
  }

  const namespace = option.metadata.namespace ?? 'default';
  const title = option.metadata.title ?? option.metadata.name;
  const ref = `resource:${namespace}/${option.metadata.name}`;

  return {
    ref,
    label: title,
  };
}

function buildCatalogEntityPath(entityRef: string): string | undefined {
  const [kindPart, namePart] = entityRef.split(':', 2);
  const [namespace, name] = (namePart ?? '').split('/', 2);

  if (!kindPart || !namespace || !name) {
    return undefined;
  }

  return `/catalog/${namespace.toLocaleLowerCase(
    'en-US',
  )}/${kindPart.toLocaleLowerCase('en-US')}/${name}`;
}

function buildSubjects(
  subjectRefs: string[],
  subjectOptions: CatalogSubjectOption[],
): AccessBindingSubject[] {
  const optionMap = new Map(subjectOptions.map(option => [option.ref, option]));

  return subjectRefs.map(ref => {
    const option = optionMap.get(ref);
    if (option) {
      return { kind: option.kind, ref: option.ref };
    }

    const prefix = ref.split(':', 1)[0]?.toLowerCase();
    return {
      kind: prefix === 'user' ? 'User' : 'Group',
      ref,
    };
  });
}

function getBindingPhase(
  binding: AccessBindingResource,
): AccessResourceStatus['phase'] {
  return binding.status?.phase ?? 'pending';
}

function buildBindingSearchText(binding: AccessBindingResource): string {
  const values = [
    binding.metadata.name,
    binding.spec.roleRef.name,
    binding.spec.targetRef?.ref,
    ...binding.spec.subjects.map(subject => subject.ref),
    binding.status?.phase,
    binding.status?.message,
  ];

  return values.filter(Boolean).join(' ').toLocaleLowerCase('en-US');
}

function hasGitflicPermission(role: AccessRoleResource): boolean {
  return Boolean(role.spec.permissions?.gitflic?.role);
}

function hasBootsmanClusterRole(role: AccessRoleResource): boolean {
  return Boolean(
    role.spec.permissions?.bootsman?.grants.some(
      grant =>
        grant.level === 'cluster' &&
        grant.assignment.kind === 'bootsmanClusterRole',
    ),
  );
}

function hasBootsmanProjectRole(role: AccessRoleResource): boolean {
  return Boolean(
    role.spec.permissions?.bootsman?.grants.some(
      grant =>
        grant.level === 'project' &&
        grant.assignment.kind === 'bootsmanProjectRole',
    ),
  );
}

function roleMatchesTargetKind(
  role: AccessRoleResource,
  targetKind: BindingTargetKind,
): boolean {
  if (targetKind === 'Cluster') {
    return hasBootsmanClusterRole(role);
  }

  if (targetKind === 'Project') {
    return hasBootsmanProjectRole(role);
  }

  return hasGitflicPermission(role);
}

function mergeFetchedBindingWithSignalState(
  fetchedBinding: AccessBindingResource,
  signal: AccessResourceSignal,
): AccessBindingResource {
  const fetchedChangedAt =
    fetchedBinding.status?.lastReconciledAt ??
    fetchedBinding.metadata.updatedAt;

  if (
    fetchedChangedAt &&
    Date.parse(fetchedChangedAt) >= Date.parse(signal.data.changedAt)
  ) {
    return fetchedBinding;
  }

  const nextPhase = signal.data.phase ?? fetchedBinding.status?.phase;
  const nextStatus = nextPhase
    ? {
        ...fetchedBinding.status,
        phase: nextPhase,
        message: signal.data.message ?? fetchedBinding.status?.message,
        lastRunId: signal.data.runId ?? fetchedBinding.status?.lastRunId,
        lastReconciledAt:
          signal.data.changedAt ?? fetchedBinding.status?.lastReconciledAt,
      }
    : fetchedBinding.status;

  return {
    ...fetchedBinding,
    metadata: {
      ...fetchedBinding.metadata,
      updatedAt: signal.data.changedAt,
    },
    status: nextStatus,
  };
}

function sortByField<T>(
  items: T[],
  field: SortField,
  direction: SortDirection,
  getSystem: (item: T) => string,
): T[] {
  const factor = direction === 'asc' ? 1 : -1;
  return [...items].sort((left, right) => {
    const leftValue =
      field === 'name'
        ? String((left as { metadata: { name: string } }).metadata.name)
        : getSystem(left);
    const rightValue =
      field === 'name'
        ? String((right as { metadata: { name: string } }).metadata.name)
        : getSystem(right);
    return leftValue.localeCompare(rightValue) * factor;
  });
}

function slugifyNamePart(value: string, maxLength: number): string {
  const normalized = value
    .trim()
    .toLocaleLowerCase('en-US')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!normalized) {
    return '';
  }

  return normalized.slice(0, maxLength).replace(/-+$/g, '');
}

function getRefName(ref: string): string {
  const [, entityRef = ''] = ref.split(':', 2);
  const [, name = entityRef] = entityRef.split('/', 2);
  return name || entityRef;
}

function buildSubjectNameSegment(subjectRefs: string[]): string {
  const subjectNames = subjectRefs
    .map(subjectRef => slugifyNamePart(getRefName(subjectRef), 16))
    .filter(Boolean);

  if (subjectNames.length === 0) {
    return '';
  }

  if (subjectNames.length === 1) {
    return subjectNames[0];
  }

  return `${subjectNames[0]}-plus-${subjectNames.length - 1}`;
}

function createShortRandomSuffix(length = 5): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const cryptoObject =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;

  if (cryptoObject?.getRandomValues) {
    const values = new Uint8Array(length);
    cryptoObject.getRandomValues(values);
    return Array.from(values, value => alphabet[value % alphabet.length]).join(
      '',
    );
  }

  return Array.from(
    { length },
    () => alphabet[Math.floor(Math.random() * alphabet.length)],
  ).join('');
}

function buildGeneratedBindingName(
  form: BindingFormState,
  lockedTargetRef?: string,
): string {
  const targetRef = lockedTargetRef ?? form.targetRef;
  const segments = [
    slugifyNamePart(form.roleName, 24),
    buildSubjectNameSegment(form.subjectRefs),
    slugifyNamePart(getRefName(targetRef), 20),
    createShortRandomSuffix(),
  ].filter(Boolean);

  return segments.join('-');
}

export const AccessBindingsManager = ({
  title,
  filterTargetRef,
  filterSubjectRef,
  lockTargetRef = false,
  defaultTargetRef,
  relatedProjectRefs,
  allowedTargetKinds,
  initialSubjectRef,
  routeBindingId,
  routeMode = 'list',
  roleEditHref,
  onEditBindingNavigate,
  onCloseBindingNavigate,
}: AccessBindingsManagerProps) => {
  const classes = useStyles();
  const api = useApi(accessApiRef);
  const { t } = useTranslationRef(accessTranslationRef);
  const catalogApi = useApi(catalogApiRef);
  const { allowed: canCreateBinding, loading: isCreatePermissionLoading } =
    usePermission({ permission: accessBindingCreatePermission });
  const { allowed: canUpdateBinding, loading: isUpdatePermissionLoading } =
    usePermission({ permission: accessBindingUpdatePermission });
  const { allowed: canDeleteBinding } = usePermission({
    permission: accessBindingDeletePermission,
  });
  const { allowed: canUpdateRole } = usePermission({
    permission: accessRoleUpdatePermission,
  });
  const { allowed: canRunReconcile } = usePermission({
    permission: accessReconcileRunPermission,
  });
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [toast, setToast] = useState<AccessToastState>({
    message: '',
    severity: 'success',
  });
  const [roles, setRoles] = useState<AccessRoleResource[]>([]);
  const [bindings, setBindings] = useState<AccessBindingResource[]>([]);
  const [bindingMaterializations, setBindingMaterializations] = useState<
    AccessReconcileMaterialization[]
  >([]);
  const [subjectOptions, setSubjectOptions] = useState<CatalogSubjectOption[]>(
    [],
  );
  const [componentOptions, setComponentOptions] = useState<
    CatalogComponentOption[]
  >([]);
  const [clusterOptions, setClusterOptions] = useState<CatalogClusterOption[]>(
    [],
  );
  const [projectOptions, setProjectOptions] = useState<CatalogProjectOption[]>(
    [],
  );
  const projectRefs = useMemo(
    () => projectOptions.map(option => option.ref),
    [projectOptions],
  );
  const [selectedBindingId, setSelectedBindingId] = useState<
    string | undefined
  >();
  const [isBindingFormOpen, setIsBindingFormOpen] = useState(false);
  const [bindingPage, setBindingPage] = useState(0);
  const [bindingSortField, setBindingSortField] = useState<SortField>('name');
  const [bindingSortDirection, setBindingSortDirection] =
    useState<SortDirection>('asc');
  const [bindingQuery, setBindingQuery] = useState('');
  const [bindingPhaseFilter, setBindingPhaseFilter] = useState<
    BindingFilterValue | AccessResourceStatus['phase']
  >('all');
  const [bindingTargetKindFilter, setBindingTargetKindFilter] = useState<
    BindingFilterValue | BindingTargetKind
  >('all');
  const [bindingRoleFilter, setBindingRoleFilter] = useState<
    BindingFilterValue | string
  >('all');
  const [bindingSubjectFilter, setBindingSubjectFilter] = useState<
    BindingFilterValue | string
  >('all');
  const [bindingGroupBy, setBindingGroupBy] = useState<BindingGroupBy>('none');
  const [highlightedBindingKeys, setHighlightedBindingKeys] = useState<
    Record<string, boolean>
  >({});
  const [signalDebugState, setSignalDebugState] =
    useState<BindingSignalDebugState>({});
  const [bindingForm, setBindingForm] = useState<BindingFormState>(
    bindingToFormState(
      undefined,
      lockTargetRef ? filterTargetRef : undefined,
      initialSubjectRef,
      defaultTargetRef,
      projectRefs,
    ),
  );
  const [hasAttemptedBindingSave, setHasAttemptedBindingSave] = useState(false);
  const bindingSignalRevisionsRef = useRef<
    Record<string, ProcessedAccessSignal>
  >({});
  const bindingFlashTimeoutsRef = useRef<Record<string, number>>({});
  const previousRouteBindingIdRef = useRef<string | undefined>(routeBindingId);
  const { lastSignal: lastResourceSignal, isSignalsAvailable } =
    useSignal<AccessResourceSignal>(ACCESS_RESOURCE_SIGNAL_CHANNEL);
  const isSignalDebugEnabled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('accessDebugSignals') ===
      '1';

  const showSuccessToast = (message: string) => {
    setToast({ message, severity: 'success' });
  };

  const showErrorToast = (message: string) => {
    setToast({ message, severity: 'error' });
  };

  const resolvedTitle = title ?? t('bindings.cardTitle');
  const resolveRoleEditHref =
    roleEditHref ??
    ((roleName: string) =>
      canUpdateRole
        ? `/access/roles/${encodeURIComponent(roleName)}/edit`
        : `/access/roles/${encodeURIComponent(roleName)}`);

  const getTargetKindLabel = (kind: BindingTargetKind) => {
    switch (kind) {
      case 'Cluster':
        return t('options.cluster');
      case 'Project':
        return t('options.project');
      default:
        return t('options.component');
    }
  };

  const targetKindOptions: readonly BindingTargetKind[] =
    allowedTargetKinds ?? ['Component', 'Cluster', 'Project'];

  const handleToastClose = () => {
    setToast(current => ({ ...current, message: '' }));
  };

  const logSignalDebug = useCallback(
    (messageText: string, payload?: unknown) => {
      if (!isSignalDebugEnabled) {
        return;
      }

      // Keep console output opt-in to avoid noisy local logs by default.
      // eslint-disable-next-line no-console
      console.debug(`[access-bindings-signals] ${messageText}`, payload);
    },
    [isSignalDebugEnabled],
  );

  const markBindingHighlighted = (binding: AccessBindingResource) => {
    const bindingKey = getManagedResourceKey(binding);
    const previousTimer = bindingFlashTimeoutsRef.current[bindingKey];
    if (previousTimer) {
      window.clearTimeout(previousTimer);
    }

    setHighlightedBindingKeys(current => ({
      ...current,
      [bindingKey]: true,
    }));

    bindingFlashTimeoutsRef.current[bindingKey] = window.setTimeout(() => {
      setHighlightedBindingKeys(current => {
        if (!current[bindingKey]) {
          return current;
        }

        const next = { ...current };
        delete next[bindingKey];
        return next;
      });
      delete bindingFlashTimeoutsRef.current[bindingKey];
    }, 2000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      const [nextRoles, nextBindings, nextMaterializations] = await Promise.all(
        [
          api.getRoles(),
          api.getBindings(),
          api.getReconcileMaterializations({
            ownerKind: 'AccessBinding',
            taskId: 'cluster-rbac',
            status: 'applied',
            limit: 1000,
          }),
        ],
      );
      setRoles(nextRoles);
      setBindings(nextBindings);
      setBindingMaterializations(nextMaterializations);
    } catch (loadError) {
      setError(loadError as Error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    const flashTimeouts = bindingFlashTimeoutsRef.current;

    return () => {
      Object.values(flashTimeouts).forEach(timeoutId =>
        window.clearTimeout(timeoutId),
      );
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!canCreateBinding && !canUpdateBinding) {
      setCatalogLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadSubjects = async () => {
      setCatalogLoading(true);

      try {
        const response = await catalogApi.getEntities();
        const nextSubjectOptions = response.items
          .map(entity => buildSubjectRef(entity))
          .filter((value): value is CatalogSubjectOption => Boolean(value))
          .sort((left, right) => left.label.localeCompare(right.label));
        const nextComponentOptions = response.items
          .map(entity => buildComponentRef(entity))
          .filter((value): value is CatalogComponentOption => Boolean(value))
          .sort((left, right) => left.label.localeCompare(right.label));
        const nextClusterOptions = response.items
          .map(entity => buildClusterRef(entity))
          .filter((value): value is CatalogClusterOption => Boolean(value))
          .sort((left, right) => left.label.localeCompare(right.label));
        const allProjectOptions = response.items
          .map(entity => buildProjectRef(entity))
          .filter((value): value is CatalogProjectOption => Boolean(value))
          .sort((left, right) => left.label.localeCompare(right.label));
        const nextProjectOptions = filterRelatedProjectOptions(
          allProjectOptions,
          relatedProjectRefs,
        );

        if (!cancelled) {
          setSubjectOptions(nextSubjectOptions);
          setComponentOptions(nextComponentOptions);
          setClusterOptions(nextClusterOptions);
          setProjectOptions(nextProjectOptions);
        }
      } catch (loadError) {
        if (!cancelled) {
          showErrorToast(
            loadError instanceof Error ? loadError.message : String(loadError),
          );
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    };

    loadSubjects();

    return () => {
      cancelled = true;
    };
  }, [canCreateBinding, canUpdateBinding, catalogApi, relatedProjectRefs]);

  const routeScopedBindings = useMemo(() => {
    return bindings.filter(binding => {
      const matchesTarget = !filterTargetRef
        ? true
        : (() => {
            const bindingTargetRef = binding.spec.targetRef?.ref;
            return (
              typeof bindingTargetRef === 'string' &&
              (normalizeEntityRef(bindingTargetRef) ===
                normalizeEntityRef(filterTargetRef) ||
                (relatedProjectRefs !== undefined &&
                  projectRefs.some(
                    projectRef =>
                      normalizeEntityRef(projectRef) ===
                      normalizeEntityRef(bindingTargetRef),
                  )))
            );
          })();

      const matchesSubject = !filterSubjectRef
        ? true
        : binding.spec.subjects.some(
            subject =>
              normalizeEntityRef(subject.ref) ===
              normalizeEntityRef(filterSubjectRef),
          );

      return matchesTarget && matchesSubject;
    });
  }, [
    bindings,
    filterSubjectRef,
    filterTargetRef,
    projectRefs,
    relatedProjectRefs,
  ]);

  const availableRoleFilters = useMemo(() => {
    return Array.from(
      new Set(routeScopedBindings.map(binding => binding.spec.roleRef.name)),
    ).sort((left, right) => left.localeCompare(right));
  }, [routeScopedBindings]);

  const availableSubjectFilters = useMemo(() => {
    return Array.from(
      new Set(
        routeScopedBindings.flatMap(binding =>
          binding.spec.subjects.map(subject => subject.ref),
        ),
      ),
    ).sort((left, right) => left.localeCompare(right));
  }, [routeScopedBindings]);

  const filteredBindings = useMemo(() => {
    const normalizedQuery = bindingQuery.trim().toLocaleLowerCase('en-US');

    return routeScopedBindings.filter(binding => {
      if (bindingPhaseFilter !== 'all') {
        if (getBindingPhase(binding) !== bindingPhaseFilter) {
          return false;
        }
      }

      if (bindingTargetKindFilter !== 'all') {
        if (
          getBindingTargetKind(binding.spec.targetRef, projectRefs) !==
          bindingTargetKindFilter
        ) {
          return false;
        }
      }

      if (
        bindingRoleFilter !== 'all' &&
        binding.spec.roleRef.name !== bindingRoleFilter
      ) {
        return false;
      }

      if (
        bindingSubjectFilter !== 'all' &&
        !binding.spec.subjects.some(
          subject => subject.ref === bindingSubjectFilter,
        )
      ) {
        return false;
      }

      if (
        normalizedQuery &&
        !buildBindingSearchText(binding).includes(normalizedQuery)
      ) {
        return false;
      }

      return true;
    });
  }, [
    bindingPhaseFilter,
    bindingQuery,
    bindingRoleFilter,
    bindingSubjectFilter,
    bindingTargetKindFilter,
    projectRefs,
    routeScopedBindings,
  ]);

  const effectiveRoleTargetKind = useMemo<BindingTargetKind>(() => {
    if (lockTargetRef && filterTargetRef) {
      return getBindingTargetKind(
        {
          kind: 'Resource',
          ref: filterTargetRef,
        },
        projectRefs,
      );
    }

    return bindingForm.targetKind;
  }, [bindingForm.targetKind, filterTargetRef, lockTargetRef, projectRefs]);

  const filteredRoleOptions = useMemo(() => {
    const matchingRoles = roles.filter(role =>
      roleMatchesTargetKind(role, effectiveRoleTargetKind),
    );

    if (!bindingForm.roleName) {
      return matchingRoles;
    }

    const selectedRoleOption = roles.find(
      role => role.metadata.name === bindingForm.roleName,
    );
    if (!selectedRoleOption) {
      return matchingRoles;
    }

    const hasSelectedRole = matchingRoles.some(
      role => role.metadata.name === selectedRoleOption.metadata.name,
    );

    return hasSelectedRole
      ? matchingRoles
      : [selectedRoleOption, ...matchingRoles];
  }, [bindingForm.roleName, effectiveRoleTargetKind, roles]);

  const rbacRoleRefsByBindingRef = useMemo(() => {
    const nextMap = new Map<string, string>();

    for (const item of bindingMaterializations) {
      if (
        item.materializationType !== 'rbac-role' ||
        !item.externalRef ||
        item.ownerKind !== 'AccessBinding'
      ) {
        continue;
      }

      nextMap.set(item.ownerRef, item.externalRef);
    }

    return nextMap;
  }, [bindingMaterializations]);

  const selectedBinding = useMemo(
    () =>
      routeScopedBindings.find(
        binding => binding.metadata.name === selectedBindingId,
      ),
    [routeScopedBindings, selectedBindingId],
  );

  const sortedBindings = useMemo(
    () =>
      sortByField(
        filteredBindings,
        bindingSortField,
        bindingSortDirection,
        binding => binding.spec.roleRef.name,
      ),
    [filteredBindings, bindingSortField, bindingSortDirection],
  );

  const pagedBindings = useMemo(
    () =>
      sortedBindings.slice(
        bindingPage * PAGE_SIZE,
        (bindingPage + 1) * PAGE_SIZE,
      ),
    [sortedBindings, bindingPage],
  );

  const groupedPagedBindings = useMemo(() => {
    if (bindingGroupBy === 'none') {
      return [
        {
          key: 'all',
          label: '',
          failedCount: pagedBindings.filter(
            binding =>
              getBindingPhase(binding) === 'failed' ||
              getBindingPhase(binding) === 'delete_failed',
          ).length,
          items: pagedBindings,
        },
      ] as GroupedBindings[];
    }

    const groups = new Map<string, GroupedBindings>();
    for (const binding of pagedBindings) {
      const phase = getBindingPhase(binding);
      let groupLabel = binding.spec.roleRef.name;
      if (bindingGroupBy === 'phase') {
        groupLabel = phase;
      } else if (bindingGroupBy === 'target') {
        groupLabel = binding.spec.targetRef?.ref ?? t('common.empty');
      }
      const groupKey = `${bindingGroupBy}:${groupLabel}`;
      const current = groups.get(groupKey);
      const isFailed = phase === 'failed' || phase === 'delete_failed';
      if (!current) {
        groups.set(groupKey, {
          key: groupKey,
          label: groupLabel,
          failedCount: isFailed ? 1 : 0,
          items: [binding],
        });
        continue;
      }

      current.items.push(binding);
      if (isFailed) {
        current.failedCount += 1;
      }
    }

    return Array.from(groups.values());
  }, [bindingGroupBy, pagedBindings, t]);

  const failedFilteredBindingsCount = useMemo(
    () =>
      filteredBindings.filter(
        binding =>
          getBindingPhase(binding) === 'failed' ||
          getBindingPhase(binding) === 'delete_failed',
      ).length,
    [filteredBindings],
  );

  const bindingNameError = useMemo(
    () => validateAccessResourceName(bindingForm.name),
    [bindingForm.name],
  );
  const bindingNameErrorMessage = useMemo(() => {
    if (!bindingNameError) {
      return undefined;
    }

    if (bindingNameError === 'metadata.name is required') {
      return t('bindings.nameRequired');
    }

    if (
      bindingNameError ===
      `metadata.name must be no more than ${ACCESS_RESOURCE_NAME_MAX_LENGTH} characters`
    ) {
      return t('bindings.nameTooLong', {
        max: String(ACCESS_RESOURCE_NAME_MAX_LENGTH),
      });
    }

    if (
      bindingNameError.startsWith(
        'metadata.name must be a lowercase RFC 1123 subdomain.',
      )
    ) {
      return t('bindings.namePattern', {
        rule: ACCESS_RESOURCE_NAME_RULE_DESCRIPTION,
      });
    }

    return bindingNameError;
  }, [bindingNameError, t]);
  const effectiveBindingTargetRef = lockTargetRef
    ? filterTargetRef ?? ''
    : bindingForm.targetRef;
  const requiredFieldErrors = validateAccessBindingRequiredFields({
    subjectRefs: bindingForm.subjectRefs,
    roleName: bindingForm.roleName,
    targetKind: bindingForm.targetKind,
    targetRef: effectiveBindingTargetRef,
  });
  const subjectsError = requiredFieldErrors.missingSubjects
    ? t('bindings.subjectRequired')
    : undefined;
  const roleError = requiredFieldErrors.missingRole
    ? t('bindings.roleRequired')
    : undefined;
  const targetRefError = requiredFieldErrors.missingTarget
    ? t('bindings.targetRequired')
    : undefined;
  const canGenerateBindingName =
    !selectedBinding &&
    Boolean(bindingForm.roleName.trim()) &&
    bindingForm.subjectRefs.length > 0 &&
    Boolean((lockTargetRef ? filterTargetRef : bindingForm.targetRef)?.trim());

  useEffect(() => {
    setBindingForm(
      bindingToFormState(
        selectedBinding,
        lockTargetRef ? filterTargetRef : undefined,
        initialSubjectRef,
        defaultTargetRef,
        projectRefs,
      ),
    );
  }, [
    defaultTargetRef,
    filterTargetRef,
    initialSubjectRef,
    lockTargetRef,
    projectRefs,
    selectedBinding,
  ]);

  useEffect(() => {
    setBindingPage(0);
  }, [
    bindingPhaseFilter,
    bindingQuery,
    bindingRoleFilter,
    bindingSubjectFilter,
    bindingTargetKindFilter,
    filterTargetRef,
    filterSubjectRef,
  ]);

  useEffect(() => {
    if (routeMode === 'create') {
      setSelectedBindingId(undefined);
      setIsBindingFormOpen(true);
      previousRouteBindingIdRef.current = undefined;
      return;
    }

    if (routeBindingId) {
      setSelectedBindingId(decodeURIComponent(routeBindingId));
      setIsBindingFormOpen(true);
      previousRouteBindingIdRef.current = routeBindingId;
      return;
    }

    const hadRouteBindingId = Boolean(previousRouteBindingIdRef.current);
    previousRouteBindingIdRef.current = routeBindingId;

    if (hadRouteBindingId && onCloseBindingNavigate) {
      setSelectedBindingId(undefined);
      setIsBindingFormOpen(false);
      setBindingForm(
        bindingToFormState(
          undefined,
          lockTargetRef ? filterTargetRef : undefined,
          initialSubjectRef,
          defaultTargetRef,
          projectRefs,
        ),
      );
    }
  }, [
    defaultTargetRef,
    filterTargetRef,
    initialSubjectRef,
    lockTargetRef,
    onCloseBindingNavigate,
    projectRefs,
    routeBindingId,
    routeMode,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (
      !lastResourceSignal ||
      lastResourceSignal.name !== 'access.resource.change' ||
      lastResourceSignal.data.resourceKind !== 'AccessBinding'
    ) {
      return () => {
        cancelled = true;
      };
    }

    const signalKey = getManagedResourceSignalKey(lastResourceSignal);
    const previousRevision = bindingSignalRevisionsRef.current[signalKey];
    setSignalDebugState(current => ({
      ...current,
      lastRevision: lastResourceSignal.revision,
      lastEvent: lastResourceSignal.data.event,
      lastResourceRef: lastResourceSignal.data.resourceRef,
      lastReceivedAt: new Date().toISOString(),
      lastError: undefined,
    }));
    logSignalDebug('received signal', lastResourceSignal);

    if (!isSignalNewerThanPrevious(previousRevision, lastResourceSignal)) {
      logSignalDebug('ignored stale or duplicate signal', {
        previousRevision,
        nextRevision: lastResourceSignal.revision,
      });
      return () => {
        cancelled = true;
      };
    }

    bindingSignalRevisionsRef.current[signalKey] = {
      revision: lastResourceSignal.revision,
      changedAt: lastResourceSignal.data.changedAt,
    };

    if (lastResourceSignal.data.event === 'deleted') {
      setBindings(current =>
        removeManagedResourceBySignal(current, lastResourceSignal),
      );
      setBindingMaterializations(current =>
        current.filter(
          item =>
            !(
              item.ownerKind === 'AccessBinding' &&
              item.ownerRef === lastResourceSignal.data.resourceRef &&
              item.taskId === 'cluster-rbac'
            ),
        ),
      );
      setSignalDebugState(current => ({
        ...current,
        lastAppliedPhase: 'deleted',
        lastAppliedName: lastResourceSignal.data.resourceName,
      }));
      logSignalDebug('removed binding from local state', {
        resourceRef: lastResourceSignal.data.resourceRef,
        resourceName: lastResourceSignal.data.resourceName,
      });

      if (
        selectedBindingId &&
        (selectedBindingId === lastResourceSignal.data.resourceRef ||
          selectedBindingId === lastResourceSignal.data.resourceName)
      ) {
        if (onCloseBindingNavigate) {
          onCloseBindingNavigate();
        } else {
          setSelectedBindingId(undefined);
          setBindingForm(
            bindingToFormState(
              undefined,
              lockTargetRef ? filterTargetRef : undefined,
              initialSubjectRef,
              defaultTargetRef,
              projectRefs,
            ),
          );
          setIsBindingFormOpen(false);
        }
      }

      return () => {
        cancelled = true;
      };
    }

    const syncBinding = async () => {
      try {
        setBindings(current =>
          patchManagedResourceBySignal(current, lastResourceSignal),
        );
        setSignalDebugState(current => ({
          ...current,
          lastFetchStartedAt: new Date().toISOString(),
          lastError: undefined,
        }));
        logSignalDebug('fetching binding by id', {
          resourceRef: lastResourceSignal.data.resourceRef,
        });

        const [nextBinding, nextBindingMaterializations] = await Promise.all([
          api.getBinding(lastResourceSignal.data.resourceRef),
          api.getReconcileMaterializations({
            ownerKind: 'AccessBinding',
            taskId: 'cluster-rbac',
            status: 'applied',
            limit: 1000,
          }),
        ]);
        if (cancelled) {
          logSignalDebug('fetch finished after cancellation', {
            resourceRef: lastResourceSignal.data.resourceRef,
          });
          return;
        }

        const syncedBinding = mergeFetchedBindingWithSignalState(
          nextBinding,
          lastResourceSignal,
        );
        setBindings(current => upsertManagedResource(current, syncedBinding));
        setBindingMaterializations(nextBindingMaterializations);
        markBindingHighlighted(syncedBinding);
        setSignalDebugState(current => ({
          ...current,
          lastFetchFinishedAt: new Date().toISOString(),
          lastFetchedPhase: nextBinding.status?.phase,
          lastAppliedPhase: syncedBinding.status?.phase,
          lastAppliedName: syncedBinding.metadata.name,
          lastError: undefined,
        }));
        logSignalDebug('patched binding row from API response', {
          binding: syncedBinding,
          materializations: nextBindingMaterializations,
        });
      } catch (syncError) {
        if (cancelled) {
          return;
        }

        const errorText =
          syncError instanceof Error ? syncError.message : String(syncError);
        showErrorToast(errorText);
        setSignalDebugState(current => ({
          ...current,
          lastFetchFinishedAt: new Date().toISOString(),
          lastError: errorText,
        }));
        logSignalDebug('failed to fetch binding for signal patch', {
          resourceRef: lastResourceSignal.data.resourceRef,
          error: errorText,
        });
      }
    };

    void syncBinding();

    return () => {
      cancelled = true;
    };
  }, [
    api,
    filterTargetRef,
    defaultTargetRef,
    initialSubjectRef,
    lastResourceSignal,
    lockTargetRef,
    logSignalDebug,
    onCloseBindingNavigate,
    projectRefs,
    selectedBindingId,
  ]);

  const handleBindingSort = (field: SortField) => {
    if (bindingSortField === field) {
      setBindingSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setBindingSortField(field);
      setBindingSortDirection('asc');
    }
    setBindingPage(0);
  };

  const handleNewBinding = () => {
    if (!canCreateBinding) {
      return;
    }

    setSelectedBindingId(undefined);
    setHasAttemptedBindingSave(false);
    setBindingForm(
      bindingToFormState(
        undefined,
        lockTargetRef ? filterTargetRef : undefined,
        initialSubjectRef,
        defaultTargetRef,
        projectRefs,
      ),
    );
    setIsBindingFormOpen(true);
  };

  const handleEditBinding = (binding: AccessBindingResource) => {
    if (onEditBindingNavigate) {
      onEditBindingNavigate(binding, canUpdateBinding ? 'edit' : 'detail');
      return;
    }

    setSelectedBindingId(binding.metadata.name);
    setHasAttemptedBindingSave(false);
    setIsBindingFormOpen(true);
  };

  const handleCancelBinding = () => {
    if ((routeBindingId || routeMode === 'create') && onCloseBindingNavigate) {
      onCloseBindingNavigate();
      return;
    }

    setSelectedBindingId(undefined);
    setHasAttemptedBindingSave(false);
    setBindingForm(
      bindingToFormState(
        undefined,
        lockTargetRef ? filterTargetRef : undefined,
        initialSubjectRef,
        defaultTargetRef,
        projectRefs,
      ),
    );
    setIsBindingFormOpen(false);
  };

  const handleSaveBinding = async () => {
    if (selectedBinding ? !canUpdateBinding : !canCreateBinding) {
      return;
    }

    setHasAttemptedBindingSave(true);

    if (
      bindingNameError ||
      requiredFieldErrors.missingSubjects ||
      requiredFieldErrors.missingRole ||
      requiredFieldErrors.missingTarget
    ) {
      return;
    }

    try {
      let savedBindingName: string | undefined;
      const targetRef =
        lockTargetRef && filterTargetRef
          ? filterTargetRef
          : bindingForm.targetRef;
      const targetKind =
        lockTargetRef && filterTargetRef
          ? getBindingTargetKind(
              { kind: 'Resource', ref: filterTargetRef },
              projectRefs,
            )
          : bindingForm.targetKind;
      const spec: AccessBindingSpec = {
        subjects: buildSubjects(bindingForm.subjectRefs, subjectOptions),
        roleRef: { name: bindingForm.roleName },
        targetRef: buildBindingTargetRef(targetKind, targetRef),
      };

      if (selectedBinding?.metadata.name) {
        const updatedBinding = await api.updateBinding(
          selectedBinding.metadata.name,
          {
            metadata: {
              name: bindingForm.name,
              description: bindingForm.description || undefined,
            },
            spec,
          },
        );
        savedBindingName = updatedBinding.metadata.name;
        showSuccessToast(t('bindings.updated', { name: bindingForm.name }));
      } else {
        const createdBinding = await api.createBinding({
          metadata: {
            name: bindingForm.name,
            description: bindingForm.description || undefined,
          },
          spec,
        });
        savedBindingName = createdBinding.metadata.name;
        showSuccessToast(t('bindings.created', { name: bindingForm.name }));
      }

      if (canRunReconcile) {
        try {
          await api.reconcile({
            dryRun: false,
            bindingIds: savedBindingName ? [savedBindingName] : undefined,
          });
        } catch (reconcileError) {
          showErrorToast(
            reconcileError instanceof Error
              ? reconcileError.message
              : String(reconcileError),
          );
        }
      }

      await loadData();
      setHasAttemptedBindingSave(false);
      handleCancelBinding();
    } catch (saveError) {
      showErrorToast(
        saveError instanceof Error ? saveError.message : String(saveError),
      );
    }
  };

  const handleDeleteBinding = async () => {
    if (!canDeleteBinding || !selectedBinding?.metadata.name) {
      return;
    }

    try {
      const { reconcileError } = await deleteBindingAndReconcile(
        api,
        selectedBinding.metadata.name,
        canRunReconcile,
      );
      showSuccessToast(
        t('bindings.markedForDeletion', {
          name: selectedBinding.metadata.name,
        }),
      );
      if (reconcileError) {
        showErrorToast(
          reconcileError instanceof Error
            ? reconcileError.message
            : String(reconcileError),
        );
      }
      await loadData();
      handleCancelBinding();
    } catch (deleteError) {
      showErrorToast(
        deleteError instanceof Error
          ? deleteError.message
          : String(deleteError),
      );
    }
  };

  let currentTargetOptions = componentOptions;
  if (bindingForm.targetKind === 'Cluster') {
    currentTargetOptions = clusterOptions;
  } else if (bindingForm.targetKind === 'Project') {
    currentTargetOptions = projectOptions;
  }

  const selectedTargetOption =
    currentTargetOptions.find(option => option.ref === bindingForm.targetRef) ??
    null;
  let bindingNameHelperText: string = t('bindings.nameHint', {
    rule: ACCESS_RESOURCE_NAME_RULE_DESCRIPTION,
  });
  if (selectedBinding) {
    bindingNameHelperText = t('bindings.nameImmutable');
  } else if (hasAttemptedBindingSave && bindingNameError) {
    bindingNameHelperText = bindingNameErrorMessage ?? bindingNameError;
  } else if (!canGenerateBindingName) {
    bindingNameHelperText = t('bindings.nameGenerateHint');
  }

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (routeMode === 'create' || routeMode === 'edit') {
    const isMutationPermissionLoading =
      routeMode === 'create'
        ? isCreatePermissionLoading
        : isUpdatePermissionLoading;
    if (isMutationPermissionLoading) {
      return null;
    }

    const canOpenMutationRoute =
      routeMode === 'create' ? canCreateBinding : canUpdateBinding;
    if (!canOpenMutationRoute) {
      return (
        <InfoCard
          title={
            routeMode === 'create'
              ? t('page.bindingCreateDeniedTitle')
              : t('page.bindingEditDeniedTitle')
          }
        >
          <Typography>
            {routeMode === 'create'
              ? t('page.bindingCreateDeniedDescription')
              : t('page.bindingEditDeniedDescription')}
          </Typography>
        </InfoCard>
      );
    }
  }

  const isReadOnlyDetail =
    routeMode === 'detail' || (!canUpdateBinding && Boolean(selectedBindingId));

  return (
    <>
      <AccessToast toast={toast} onClose={handleToastClose} />
      {isSignalDebugEnabled && (
        <Box mb={2}>
          <Typography variant="body2" className={classes.helperText}>
            {JSON.stringify(
              {
                signalsAvailable: isSignalsAvailable,
                lastRevision: signalDebugState.lastRevision,
                lastEvent: signalDebugState.lastEvent,
                lastResourceRef: signalDebugState.lastResourceRef,
                lastReceivedAt: signalDebugState.lastReceivedAt,
                lastFetchStartedAt: signalDebugState.lastFetchStartedAt,
                lastFetchFinishedAt: signalDebugState.lastFetchFinishedAt,
                lastFetchedPhase: signalDebugState.lastFetchedPhase,
                lastAppliedPhase: signalDebugState.lastAppliedPhase,
                lastAppliedName: signalDebugState.lastAppliedName,
                lastError: signalDebugState.lastError,
              },
              null,
              2,
            )}
          </Typography>
        </Box>
      )}
      <InfoCard title={resolvedTitle}>
        <Box className={classes.tableActions}>
          <Typography variant="body2" color="textSecondary">
            {t('bindings.summaryTotals', {
              showing: t('bindings.showing', {
                value: String(filteredBindings.length),
              }),
              total: String(routeScopedBindings.length),
              failed: String(failedFilteredBindingsCount),
            })}
          </Typography>
          {canCreateBinding ? (
            <Button variant="outlined" onClick={handleNewBinding}>
              {t('bindings.newBinding')}
            </Button>
          ) : null}
        </Box>
        <Box className={classes.bindingsControls}>
          <Box className={classes.bindingsFiltersRow}>
            <TextField
              select
              size="small"
              variant="outlined"
              label={t('common.status')}
              value={bindingPhaseFilter}
              onChange={event =>
                setBindingPhaseFilter(
                  event.target.value as
                    | BindingFilterValue
                    | AccessResourceStatus['phase'],
                )
              }
            >
              <MenuItem value="all">{t('bindings.filterAll')}</MenuItem>
              <MenuItem value="pending">{t('status.pending')}</MenuItem>
              <MenuItem value="reconciling">{t('status.reconciling')}</MenuItem>
              <MenuItem value="ready">{t('status.ready')}</MenuItem>
              <MenuItem value="failed">{t('status.failed')}</MenuItem>
              <MenuItem value="deleting">{t('status.deleting')}</MenuItem>
              <MenuItem value="delete_failed">
                {t('status.delete_failed')}
              </MenuItem>
            </TextField>
            <TextField
              size="small"
              variant="outlined"
              label={t('bindings.filterSearch')}
              value={bindingQuery}
              onChange={event => setBindingQuery(event.target.value)}
              placeholder="name / target / role / subject / message"
            />
            <TextField
              select
              size="small"
              variant="outlined"
              label={t('common.role')}
              value={bindingRoleFilter}
              onChange={event => setBindingRoleFilter(event.target.value)}
            >
              <MenuItem value="all">{t('bindings.filterAll')}</MenuItem>
              {availableRoleFilters.map(roleName => (
                <MenuItem key={roleName} value={roleName}>
                  {roleName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label={t('common.subjects')}
              value={bindingSubjectFilter}
              onChange={event => setBindingSubjectFilter(event.target.value)}
            >
              <MenuItem value="all">{t('bindings.filterAll')}</MenuItem>
              {availableSubjectFilters.map(subjectRef => (
                <MenuItem key={subjectRef} value={subjectRef}>
                  {subjectRef}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label={t('bindings.targetKind')}
              value={bindingTargetKindFilter}
              onChange={event =>
                setBindingTargetKindFilter(
                  event.target.value as BindingFilterValue | BindingTargetKind,
                )
              }
            >
              <MenuItem value="all">{t('bindings.filterAll')}</MenuItem>
              <MenuItem value="Component">{t('options.component')}</MenuItem>
              <MenuItem value="Cluster">{t('options.cluster')}</MenuItem>
              <MenuItem value="Project">{t('options.project')}</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label={t('bindings.filterGroupBy')}
              value={bindingGroupBy}
              onChange={event =>
                setBindingGroupBy(event.target.value as BindingGroupBy)
              }
            >
              <MenuItem value="none">{t('bindings.filterGroupNone')}</MenuItem>
              <MenuItem value="phase">
                {t('bindings.filterGroupStatus')}
              </MenuItem>
              <MenuItem value="role">{t('bindings.filterGroupRole')}</MenuItem>
              <MenuItem value="target">
                {t('bindings.filterGroupTarget')}
              </MenuItem>
            </TextField>
          </Box>
          <Box className={classes.actions}>
            <Button
              variant="text"
              color="primary"
              onClick={() => setBindingPhaseFilter('failed')}
            >
              {t('bindings.failedOnly')}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setBindingQuery('');
                setBindingPhaseFilter('all');
                setBindingTargetKindFilter('all');
                setBindingRoleFilter('all');
                setBindingSubjectFilter('all');
                setBindingGroupBy('none');
              }}
            >
              {t('bindings.clearFilters')}
            </Button>
          </Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.statusCell}>
                {t('common.status')}
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={bindingSortField === 'name'}
                  direction={
                    bindingSortField === 'name' ? bindingSortDirection : 'asc'
                  }
                  onClick={() => handleBindingSort('name')}
                >
                  {t('common.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={bindingSortField === 'role'}
                  direction={
                    bindingSortField === 'role' ? bindingSortDirection : 'asc'
                  }
                  onClick={() => handleBindingSort('role')}
                >
                  {t('common.role')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('common.subjects')}</TableCell>
              {filterTargetRef ? null : (
                <TableCell>{t('common.target')}</TableCell>
              )}
              <TableCell>{t('bindings.rbac')}</TableCell>
              <TableCell align="right" className={classes.actionsCell}>
                {t('common.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedPagedBindings.map(group => (
              <Fragment key={`group:${group.key}`}>
                {bindingGroupBy !== 'none' ? (
                  <TableRow className={classes.groupRow}>
                    <TableCell colSpan={filterTargetRef ? 6 : 7}>
                      <Typography variant="body2" className={classes.groupText}>
                        {t('bindings.groupTotals', {
                          label: group.label,
                          total: String(group.items.length),
                          failed: String(group.failedCount),
                        })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {group.items.map(binding => (
                  <TableRow
                    key={binding.metadata.name}
                    hover
                    className={
                      highlightedBindingKeys[getManagedResourceKey(binding)]
                        ? classes.highlightedRow
                        : undefined
                    }
                  >
                    <TableCell className={classes.statusCell}>
                      <AccessStatusChip status={binding.status} />
                      {(binding.status?.phase === 'failed' ||
                        binding.status?.phase === 'delete_failed') &&
                      binding.status?.message ? (
                        <Tooltip title={binding.status.message}>
                          <Typography
                            variant="caption"
                            color="error"
                            className={classes.bindingErrorText}
                          >
                            {binding.status.message}
                          </Typography>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell>{binding.metadata.name}</TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={resolveRoleEditHref(binding.spec.roleRef.name)}
                        color="primary"
                      >
                        {binding.spec.roleRef.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {binding.spec.subjects.length
                        ? binding.spec.subjects.map(subject => {
                            const href = buildCatalogEntityPath(subject.ref);

                            return (
                              <Box key={subject.ref}>
                                {href ? (
                                  <Link
                                    component={RouterLink}
                                    to={href}
                                    color="primary"
                                  >
                                    {subject.ref}
                                  </Link>
                                ) : (
                                  <Typography variant="body2">
                                    {subject.ref}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })
                        : t('common.empty')}
                    </TableCell>
                    {filterTargetRef ? null : (
                      <TableCell>
                        {binding.spec.targetRef?.ref
                          ? (() => {
                              const href = buildCatalogEntityPath(
                                binding.spec.targetRef.ref,
                              );
                              return href ? (
                                <Link
                                  component={RouterLink}
                                  to={href}
                                  color="primary"
                                >
                                  {binding.spec.targetRef.ref}
                                </Link>
                              ) : (
                                binding.spec.targetRef.ref
                              );
                            })()
                          : t('common.empty')}
                      </TableCell>
                    )}
                    <TableCell>
                      {(() => {
                        const bindingRef = binding.metadata.name;
                        const roleEntityRef =
                          rbacRoleRefsByBindingRef.get(bindingRef);
                        if (!roleEntityRef) {
                          return t('common.empty');
                        }

                        const href = buildRbacRolePath(roleEntityRef);
                        if (!href) {
                          return t('common.empty');
                        }

                        return (
                          <Link
                            component={RouterLink}
                            to={href}
                            color="primary"
                          >
                            {roleEntityRef}
                          </Link>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      <Button
                        color="primary"
                        onClick={() => handleEditBinding(binding)}
                      >
                        {canUpdateBinding ? t('common.edit') : t('common.view')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredBindings.length}
          page={bindingPage}
          onPageChange={(_event, nextPage) => setBindingPage(nextPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </InfoCard>
      <Drawer
        anchor="right"
        open={isBindingFormOpen}
        onClose={handleCancelBinding}
        classes={{ paper: classes.drawerPaper }}
      >
        <Box className={classes.drawerHeader}>
          <Typography variant="h6">
            {selectedBinding
              ? t('bindings.editBinding')
              : t('bindings.createBinding')}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={handleCancelBinding}>
            {t('common.close')}
          </Button>
        </Box>
        <Divider />
        <Box className={classes.drawerContent}>
          {isReadOnlyDetail ? (
            <InfoCard title={t('page.readOnlyTitle')}>
              <Typography>{t('page.readOnlyDescription')}</Typography>
            </InfoCard>
          ) : null}
          <Box className={classes.form}>
            <Box className={classes.nameRow}>
              <TextField
                className={classes.nameField}
                label={t('common.name')}
                value={bindingForm.name}
                disabled={isReadOnlyDetail || Boolean(selectedBinding)}
                onChange={event =>
                  setBindingForm(current => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                variant="outlined"
                size="small"
                error={Boolean(hasAttemptedBindingSave && bindingNameError)}
                helperText={bindingNameHelperText}
              />
            </Box>
            <TextField
              label={t('common.description')}
              value={bindingForm.description}
              disabled={isReadOnlyDetail}
              onChange={event =>
                setBindingForm(current => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              variant="outlined"
              size="small"
              multiline
              minRows={2}
            />
            <Autocomplete
              multiple
              options={subjectOptions}
              filterOptions={subjectFilterOptions}
              value={subjectOptions.filter(option =>
                bindingForm.subjectRefs.includes(option.ref),
              )}
              disabled={isReadOnlyDetail || catalogLoading}
              onChange={(_event, nextOptions) =>
                setBindingForm(current => ({
                  ...current,
                  subjectRefs: nextOptions.map(option => option.ref),
                }))
              }
              getOptionLabel={option => option.label}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('common.subjects')}
                  variant="outlined"
                  size="small"
                  error={Boolean(hasAttemptedBindingSave && subjectsError)}
                />
              )}
              renderOption={option => (
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option.ref}
                  </Typography>
                </Box>
              )}
            />
            {hasAttemptedBindingSave && subjectsError ? (
              <FormHelperText error>{subjectsError}</FormHelperText>
            ) : null}
            <Typography variant="caption" color="textSecondary">
              {catalogLoading
                ? t('bindings.subjectsHelpLoading')
                : t('bindings.subjectsHelpReady')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  select
                  disabled={
                    isReadOnlyDetail ||
                    lockTargetRef ||
                    Boolean(selectedBinding)
                  }
                  label={t('bindings.targetKind')}
                  value={bindingForm.targetKind}
                  onChange={event =>
                    setBindingForm(current => ({
                      ...current,
                      targetKind: event.target.value as BindingTargetKind,
                      targetRef: '',
                    }))
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {targetKindOptions.map(targetKind => (
                    <MenuItem key={targetKind} value={targetKind}>
                      {getTargetKindLabel(targetKind)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  select
                  label={t('common.role')}
                  value={bindingForm.roleName}
                  disabled={isReadOnlyDetail}
                  onChange={event =>
                    setBindingForm(current => ({
                      ...current,
                      roleName: event.target.value,
                    }))
                  }
                  variant="outlined"
                  size="small"
                  error={Boolean(hasAttemptedBindingSave && roleError)}
                  fullWidth
                >
                  {filteredRoleOptions.map(role => (
                    <MenuItem
                      key={role.metadata.uid ?? role.metadata.name}
                      value={role.metadata.name}
                    >
                      {role.metadata.name}
                    </MenuItem>
                  ))}
                </TextField>
                {hasAttemptedBindingSave && roleError ? (
                  <FormHelperText error>{roleError}</FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={currentTargetOptions}
                  disabled={isReadOnlyDetail || lockTargetRef}
                  value={selectedTargetOption}
                  onChange={(_event, option) =>
                    setBindingForm(current => ({
                      ...current,
                      targetRef: option?.ref ?? '',
                    }))
                  }
                  getOptionLabel={option => option.label}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('bindings.targetRef')}
                      variant="outlined"
                      size="small"
                      error={Boolean(hasAttemptedBindingSave && targetRefError)}
                      helperText={
                        hasAttemptedBindingSave ? targetRefError : undefined
                      }
                      fullWidth
                    />
                  )}
                  renderOption={option => (
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                    </Box>
                  )}
                  noOptionsText={
                    bindingForm.targetKind === 'Project'
                      ? t('bindings.noProjectTargets')
                      : undefined
                  }
                />
              </Grid>
            </Grid>
            <Box className={classes.actions}>
              {!isReadOnlyDetail ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveBinding}
                >
                  {t('bindings.saveBinding')}
                </Button>
              ) : null}
              {!selectedBinding && canCreateBinding && !isReadOnlyDetail && (
                <Tooltip
                  title={
                    canGenerateBindingName
                      ? t('bindings.generateName')
                      : t('bindings.generateNameTooltip')
                  }
                >
                  <span>
                    <Button
                      className={classes.nameAction}
                      variant="outlined"
                      disabled={!canGenerateBindingName}
                      onClick={() =>
                        setBindingForm(current => ({
                          ...current,
                          name: buildGeneratedBindingName(
                            current,
                            lockTargetRef ? filterTargetRef : undefined,
                          ),
                        }))
                      }
                    >
                      {t('bindings.generateName')}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button variant="outlined" onClick={handleCancelBinding}>
                {t('common.cancel')}
              </Button>
              {selectedBinding && canDeleteBinding && !isReadOnlyDetail ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleDeleteBinding}
                >
                  {t('bindings.deleteBinding')}
                </Button>
              ) : null}
            </Box>
            {selectedBinding && (
              <Box>
                <Typography variant="subtitle2">
                  {t('common.currentResource')}
                </Typography>
                <Typography variant="body2" className={classes.jsonPreview}>
                  {JSON.stringify(selectedBinding, null, 2)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
